import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Factory, Buildings, HardHat, PencilRuler, MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getApiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suggestions only - task_type is now free text, "Visita" is just the default value
export const TASK_TYPE_SUGGESTIONS = ["Visita", "Chamada", "Email", "Cotização"];

const REMINDER_OPTIONS = [
  { value: "15", label: "15 min antes" },
  { value: "30", label: "30 min antes" },
  { value: "60", label: "1h antes" },
  { value: "120", label: "2h antes" },
  { value: "1440", label: "1 dia antes" },
  { value: "2880", label: "2 dias antes" },
];

const getEntityIcon = (entityType, gabineteType) => {
  if (entityType === "client") return User;
  if (entityType === "gabinete") {
    if (gabineteType === "arquiteto") return PencilRuler;
    if (gabineteType === "construtor") return HardHat;
    return Buildings;
  }
  return Factory;
};

// Shared task create/edit dialog, used by the weekly Calendário tab and the
// client detail "Agendar" tab.
export function TaskFormDialog({ open, onClose, onSaved, task = null, defaultEntity = null, defaultDate = null }) {
  const [entities, setEntities] = useState([]);
  const [entitySearch, setEntitySearch] = useState("");
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [editingEntity, setEditingEntity] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: "", gabinete_id: "", custom_name: "",
    task_type: "Visita", scheduled_date: "", scheduled_time: "", notes: "", status: "pendente", reminder_minutes: "30",
  });

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/visits/entities`).then((res) => setEntities(res.data)).catch(() => {});

    if (task) {
      setForm({
        client_id: task.client_id || "",
        gabinete_id: task.gabinete_id || "",
        custom_name: task.custom_name || "",
        task_type: task.task_type || "Visita",
        scheduled_date: task.scheduled_date || "",
        scheduled_time: task.scheduled_time || "",
        notes: task.notes || "",
        status: task.status || "pendente",
        reminder_minutes: String(task.reminder_minutes || 30),
      });
      setEntitySearch(task.entity_name || task.custom_name || "");
      setEditingEntity(false);
    } else {
      setForm({
        client_id: defaultEntity?.type === "client" ? defaultEntity.id : "",
        gabinete_id: defaultEntity?.type === "gabinete" ? defaultEntity.id : "",
        custom_name: "",
        task_type: "Visita",
        scheduled_date: defaultDate || new Date().toISOString().split("T")[0],
        scheduled_time: "",
        notes: "",
        status: "pendente",
        reminder_minutes: "30",
      });
      setEntitySearch(defaultEntity?.name || "");
      setEditingEntity(!defaultEntity);
    }
  }, [open, task, defaultEntity, defaultDate]);

  const filteredEntities = entities.filter((e) =>
    e.name.toLowerCase().includes(entitySearch.toLowerCase())
  );

  const selectEntity = (entity) => {
    if (entity.type === "client") {
      setForm((prev) => ({ ...prev, client_id: entity.id, gabinete_id: "", custom_name: "" }));
    } else {
      setForm((prev) => ({ ...prev, client_id: "", gabinete_id: entity.id, custom_name: "" }));
    }
    setEntitySearch(entity.name);
    setShowEntityDropdown(false);
    setEditingEntity(false);
  };

  const handleEntitySearchChange = (value) => {
    setEntitySearch(value);
    setShowEntityDropdown(true);
    const match = entities.find((e) => e.name.toLowerCase() === value.toLowerCase());
    if (match) {
      selectEntity(match);
    } else {
      setForm((prev) => ({ ...prev, client_id: "", gabinete_id: "", custom_name: value }));
    }
  };

  const handleSave = async () => {
    if (!form.scheduled_date) {
      toast.error("Selecione uma data");
      return;
    }
    if (!form.client_id && !form.gabinete_id && !form.custom_name.trim()) {
      toast.error("Selecione uma entidade ou escreva um nome");
      return;
    }
    if (!form.task_type.trim()) {
      toast.error("Escreva o tipo de tarefa");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id || null,
        gabinete_id: form.gabinete_id || null,
        custom_name: (!form.client_id && !form.gabinete_id) ? form.custom_name.trim() : null,
        task_type: form.task_type.trim(),
        scheduled_date: form.scheduled_date,
        scheduled_time: form.scheduled_time || null,
        notes: form.notes,
        status: form.status,
        reminder_minutes: parseInt(form.reminder_minutes, 10) || 30,
      };
      if (task) {
        await axios.put(`${API}/tasks/${task.id}`, payload);
        toast.success("Tarefa atualizada");
      } else {
        await axios.post(`${API}/tasks`, payload);
        toast.success("Tarefa agendada");
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao guardar tarefa"));
    } finally {
      setSaving(false);
    }
  };

  const selectedEntityType = form.client_id ? "client" : form.gabinete_id ? "gabinete" : "custom";
  const SelectedIcon = getEntityIcon(
    selectedEntityType,
    entities.find((e) => e.id === form.gabinete_id)?.gabinete_type
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>{task ? "Editar Tarefa" : "Nova Tarefa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5 relative">
            <Label>Cliente / Promoção / Nome</Label>
            {!editingEntity ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-stone-200 bg-stone-50" data-testid="task-entity-display">
                <div className="flex items-center gap-2 min-w-0">
                  <SelectedIcon size={16} className="text-stone-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-stone-800 truncate">{entitySearch || "Sem nome"}</span>
                </div>
                {!defaultEntity && (
                  <button
                    type="button"
                    onClick={() => setEditingEntity(true)}
                    className="text-xs text-[#009392] font-medium hover:underline flex-shrink-0 flex items-center gap-1"
                    data-testid="btn-change-entity"
                  >
                    <PencilSimple size={12} /> Alterar
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    value={entitySearch}
                    onChange={(e) => handleEntitySearchChange(e.target.value)}
                    onFocus={() => setShowEntityDropdown(true)}
                    placeholder="Pesquisar ou escrever nome..."
                    className="pl-9"
                    autoFocus
                    data-testid="task-entity-search-input"
                  />
                </div>
                {showEntityDropdown && filteredEntities.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {filteredEntities.slice(0, 30).map((entity) => {
                      const Icon = getEntityIcon(entity.type, entity.gabinete_type);
                      return (
                        <button
                          key={entity.id}
                          type="button"
                          onClick={() => selectEntity(entity)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-stone-50 text-sm"
                          data-testid={`task-entity-option-${entity.id}`}
                        >
                          <Icon size={14} className="text-stone-400" />
                          <span>{entity.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Input
                value={form.task_type}
                onChange={(e) => setForm((p) => ({ ...p, task_type: e.target.value }))}
                placeholder="Ex: Visita, Chamada..."
                list="task-type-suggestions"
                data-testid="task-type-input"
              />
              <datalist id="task-type-suggestions">
                {TASK_TYPE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            {task && (
              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="concluida">Concluída</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm((p) => ({ ...p, scheduled_date: e.target.value }))} data-testid="task-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Hora (opcional)</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => setForm((p) => ({ ...p, scheduled_time: e.target.value }))} data-testid="task-time-input" />
            </div>
          </div>

          {form.scheduled_time && (
            <div className="space-y-1.5">
              <Label>Notificar</Label>
              <Select value={form.reminder_minutes} onValueChange={(v) => setForm((p) => ({ ...p, reminder_minutes: v }))}>
                <SelectTrigger data-testid="task-reminder-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REMINDER_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Notas</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="task-notes-input" />
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button className="flex-1 btn-primary" onClick={handleSave} disabled={saving} data-testid="task-save-button">
              {saving ? "A guardar..." : task ? "Guardar" : "Agendar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
